import React, { useState, useCallback } from 'react';
import { apiService } from '../../services/api';
import { useAnalytics } from '../../hooks/useAnalytics';

interface CSVUploadWidgetProps {
  onUploadSuccess: () => void;
  onClose: () => void;
  initialCsvContent?: string;
  initialFileName?: string;
}

const CSVUploadWidget: React.FC<CSVUploadWidgetProps> = ({ onUploadSuccess, onClose, initialCsvContent, initialFileName }) => {
  const { trackButtonClick, trackFileUpload, trackFormSubmission } = useAnalytics();
  
  // CSV headers with example data
  const csvHeaders = `Symbol,Quantity,Buy Price,Exchange
RELIANCE,100,2500.00,NSE`;
  
  const [csvData, setCsvData] = useState(initialCsvContent || csvHeaders);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [fileName, setFileName] = useState<string | null>(initialFileName || null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  
  // Sample CSV template with headers and example data
  const sampleCSVTemplate = `Symbol,Quantity,Buy Price,Exchange
RELIANCE,100,2500.00,NSE
TCS,50,3200.00,NSE
INFY,200,1500.00,NSE
HDFCBANK,75,1800.00,NSE
WIPRO,300,400.00,NSE`;

  // Function to download sample CSV file
  const downloadSampleCSV = () => {
    trackButtonClick('download_sample_csv', 'csv_upload');
    
    const blob = new Blob([sampleCSVTemplate], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'holdings_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Remove localStorage logic for dropped CSV content
  // (delete the entire React.useEffect that checks localStorage)

  // Function to process CSV content - strip quotes and handle empty rows
  const processCSVContent = (content: string): string => {
    const lines = content.trim().split('\n');
    const processedLines = lines
      .map(line => line.trim())
      .filter(line => line !== '') // Remove empty rows
      .map(line => {
        // Strip quotes from each field in the line
        const fields = line.split(',').map(field => 
          field.trim().replace(/^["']|["']$/g, '')
        );
        return fields.join(',');
      });
    return processedLines.join('\n');
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setFileName(file.name);
        const reader = new FileReader();
        reader.onload = (event) => {
          const content = event.target?.result as string;
          // Strip quotes from the entire content and handle empty rows
          const processedContent = processCSVContent(content);
          setCsvData(processedContent);
          setError(null);
          setValidationErrors([]);
        };
        reader.readAsText(file);
      } else {
        setError('Please upload a CSV file only, and check that the file ends with .csv');
      }
    }
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setFileName(file.name);
        const reader = new FileReader();
        reader.onload = (event) => {
          const content = event.target?.result as string;
          // Strip quotes from the entire content and handle empty rows
          const processedContent = processCSVContent(content);
          setCsvData(processedContent);
          setError(null);
          setValidationErrors([]);
        };
        reader.readAsText(file);
      } else {
        setError('Please upload a CSV file only, and check that the file ends with .csv');
      }
    }
  };

  const validateCSVData = (csvContent: string): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    const lines = csvContent.trim().split('\n');
    
    if (lines.length < 2) {
      errors.push('CSV must contain at least a header row and one data row');
      return { valid: false, errors };
    }

    // Parse header to find column indices
    const header = lines[0].split(',').map(h => h.trim().replace(/^["']|["']$/g, '').toLowerCase());
    const symbolIndex = header.findIndex(h => ['symbol', 'instrument', 'scrip'].includes(h));
    const quantityIndex = header.findIndex(h => ['quantity', 'qty', 'qty.'].includes(h));
    const avgPriceIndex = header.findIndex(h => ['buy price', 'average price', 'avg price', 'avg. cost', 'avg cost', 'average_price'].includes(h));

    if (symbolIndex === -1) {
      errors.push('Missing required column: Symbol/Instrument');
    }
    if (quantityIndex === -1) {
      errors.push('Missing required column: Quantity/Qty');
    }
    if (avgPriceIndex === -1) {
      errors.push('Missing required column: Buy Price/Average Price/Avg. cost');
    }

    if (errors.length > 0) {
      return { valid: false, errors };
    }

    // Validate each data row
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const values = line.split(',').map(v => v.trim().replace(/^["']|["']$/g, ''));
      const symbol = values[symbolIndex];
      const quantity = values[quantityIndex];
      const avgPrice = values[avgPriceIndex];

      // Skip rows where symbol is missing (don't show error, just ignore)
      if (!symbol) {
        continue;
      }

      const qtyNum = parseFloat(quantity);
      const priceNum = parseFloat(avgPrice);

      if (isNaN(qtyNum) || qtyNum <= 0) {
        errors.push(`Row ${i + 1} (${symbol}): Invalid quantity - must be a positive number`);
      }

      if (isNaN(priceNum) || priceNum <= 0) {
        errors.push(`Row ${i + 1} (${symbol}): Invalid average price - must be a positive number`);
      }
    }

    return { valid: errors.length === 0, errors };
  };

  const handleUpload = async () => {
    if (!csvData.trim()) {
      setError('Please provide CSV data');
      return;
    }

    // Validate CSV data
    const validation = validateCSVData(csvData);
    if (!validation.valid) {
      setValidationErrors(validation.errors);
      setError('CSV validation failed. Please fix the errors below.');
      
      // Track validation errors
      if (fileName) {
        trackFormSubmission('csv_upload', false, `Validation failed: ${validation.errors.length} errors`);
      }
      return;
    }

    setIsUploading(true);
    setError(null);
    setValidationErrors([]);

    try {
      const response = await apiService.holdings.uploadCSV(csvData);
      
      if (response.success) {
        // Track successful upload
        if (fileName) {
          trackFileUpload('csv', fileName, true);
        }
        onUploadSuccess();
        onClose();
      } else {
        const errorMsg = typeof response.error === 'string' ? response.error : 'Upload failed';
        setError(errorMsg);
        
        // Track failed upload
        if (fileName) {
          trackFileUpload('csv', fileName, false, errorMsg);
        }
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Upload failed';
      setError(typeof errorMessage === 'string' ? errorMessage : 'Upload failed');
      
      // Track failed upload
      if (fileName) {
        trackFileUpload('csv', fileName, false, errorMessage);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const csvFormat = `Symbol,Quantity,Buy Price,Exchange
RELIANCE,100,2500.00,NSE
TCS,50,3200.00,NSE
INFY,200,1500.00,NSE`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Upload Holdings</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          {/* Sample File Download Section */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-medium text-green-900 mb-2">📥 Get Started with Sample File</h3>
            <p className="text-sm text-green-800 mb-3">
              Download our sample CSV template with proper headers and example data. The first row contains column headers - you can fill in your holdings starting from the second row onwards.
            </p>
            <button
              onClick={downloadSampleCSV}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download Sample Template
            </button>
          </div>

          {/* CSV Format Info */}
          <details className="bg-blue-50 border border-blue-200 rounded-lg">
            <summary className="p-4 cursor-pointer hover:bg-blue-100 transition-colors">
              <h3 className="font-medium text-blue-900 inline-flex items-center">
                📋 Tips for easy upload
                <svg className="w-4 h-4 ml-2 transform transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </h3>
            </summary>
            <div className="p-4 pt-0 space-y-2">
              <p className="text-sm text-blue-800">
                <strong>File Format:</strong> Ensure that the file ends with .csv only. Save Excel files as CSV before uploading
              </p>
              <p className="text-sm text-blue-800">
                <strong>Mandatory fields:</strong> Symbol, Quantity, Average Buying Price
              </p>
              <p className="text-sm text-blue-800">
                <strong>Current prices:</strong> Will be automatically fetched
              </p>
              <p className="text-sm text-blue-800">
                <strong>Number of lines:</strong> You can add up to 1000 holdings at a time.
              </p>
              <p className="text-sm text-blue-800">
                <strong>Field mapping:</strong> Make sure that the first line of the file contains the headers for the fields. Supported field names are:
              </p>
              <p className="text-sm text-blue-800">
                <strong>💡 Tip:</strong> The first row should contain headers, and your data should start from the second row onwards.
              </p>
              
              <ul className="text-xs text-blue-700 list-disc list-inside">
                <li>Symbol: "Symbol", "Instrument"</li>
                <li>Quantity: "Quantity", "Qty", "Qty."</li>
                <li>Average Buying Price: "Buy Price", "Average Price", "Avg. cost", "Avg cost", "average_price"</li>
                <li>Exchange: "Exchange" (optional, defaults to NSE)</li>
              </ul>
              <details className="text-sm">
                <summary className="cursor-pointer text-blue-700 hover:text-blue-900">View Example Format</summary>
                <pre className="mt-2 text-xs bg-white p-2 rounded border overflow-x-auto">
{csvFormat}
                </pre>
              </details>
            </div>
          </details>

          {/* Drag & Drop Area */}
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              dragActive 
                ? 'border-blue-400 bg-blue-50' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {fileName ? (
              <div className="flex flex-col items-center">
                <svg className="mx-auto h-12 w-12 text-green-500 mb-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div className="bg-green-100 text-green-800 px-3 py-2 rounded-full text-sm font-medium mb-2">
                  {fileName}
                </div>
                <p className="text-sm text-gray-600">File loaded successfully</p>
                <button
                  onClick={() => {
                    trackButtonClick('remove_file', 'csv_upload');
                    setFileName(null);
                    setCsvData('');
                    setError(null);
                    setValidationErrors([]);
                  }}
                  className="mt-2 text-sm text-red-600 hover:text-red-800"
                >
                  Remove file
                </button>
              </div>
            ) : (
              <>
                <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <div className="mt-4">
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <span className="text-blue-600 hover:text-blue-500 font-medium">Click to upload</span>
                    <span className="text-gray-500"> or drag and drop</span>
                  </label>
                  <input
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    accept=".csv"
                    className="sr-only"
                    onChange={handleFileInput}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">CSV files only</p>
              </>
            )}
          </div>

          {/* Manual CSV Input */}
          <div>
            <label htmlFor="csv-data" className="block text-sm font-medium text-gray-700 mb-2">
              Or paste CSV data directly from the second line onwards:
            </label>
            <textarea
              id="csv-data"
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Paste your CSV data after the header line..."
              value={csvData}
              onChange={(e) => setCsvData(e.target.value)}
            />
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800 font-medium">{error}</p>
            </div>
          )}

          {/* Validation Errors Display */}
          {validationErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-red-800 mb-2">Validation Errors:</h4>
              <ul className="text-sm text-red-700 space-y-1 max-h-40 overflow-y-auto">
                {validationErrors.map((error, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    <span>{error}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={() => {
                trackButtonClick('cancel_upload', 'csv_upload');
                onClose();
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                trackButtonClick('upload_holdings', 'csv_upload');
                handleUpload();
              }}
              disabled={isUploading || !csvData.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? 'Uploading...' : 'Upload Holdings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CSVUploadWidget; 